import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  TextInput,
  Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { getStores, createStore, updateStore, deleteStore } from '../services/storesService';

const StoresScreen = ({ navigation, route }) => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [storeName, setStoreName] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Reagiere auf Header-Button
  useEffect(() => {
    if (route.params?.openCreateModal) {
      setModalVisible(true);
      setEditingStore(null);
      setStoreName('');
      // Parameter zurücksetzen
      navigation.setParams({ openCreateModal: false });
    }
  }, [route.params?.openCreateModal]);

  useFocusEffect(
    useCallback(() => {
      loadStores();
    }, [])
  );

  const loadStores = async () => {
    const result = await getStores();
    
    if (result.success) {
      // Nach Name sortieren
      const sortedStores = result.data.sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setStores(sortedStores);
    } else {
      Alert.alert('Fehler', result.error);
    }
    
    setLoading(false);
  };

  // Gefilterte Läden basierend auf Suchanfrage
  const filteredStores = useMemo(() => {
    if (!searchQuery.trim()) {
      return stores;
    }
    
    const query = searchQuery.toLowerCase().trim();
    return stores.filter(store => 
      store.name.toLowerCase().includes(query)
    );
  }, [stores, searchQuery]);

  const handleOpenModal = (store = null) => {
    setEditingStore(store);
    setStoreName(store ? store.name : '');
    setModalVisible(true);
  };

  const handleSave = async () => {
    if (!storeName.trim()) {
      Alert.alert('Fehler', 'Bitte gib einen Namen ein');
      return;
    }

    setSaving(true);
    const result = editingStore
      ? await updateStore(editingStore.id, storeName.trim())
      : await createStore(storeName.trim());

    setSaving(false);

    if (result.success) {
      setModalVisible(false);
      setStoreName('');
      setEditingStore(null);
      loadStores();
    } else {
      Alert.alert('Fehler', result.error);
    }
  };

  const handleDelete = (store) => {
    Alert.alert(
      'Laden löschen',
      `Möchtest du "${store.name}" wirklich löschen? Alle zugehörigen Einkaufslisten werden ebenfalls gelöscht.`,
      [
        { text: 'Abbrechen', style: 'cancel' },
        {
          text: 'Löschen',
          style: 'destructive',
          onPress: async () => {
            const result = await deleteStore(store.id);
            if (result.success) {
              loadStores();
            } else {
              Alert.alert('Fehler', result.error);
            }
          },
        },
      ]
    );
  };

  const renderStore = ({ item }) => (
    <View style={styles.storeCard}>
      <View style={styles.storeInfo}>
        <Text style={styles.storeName}>{item.name}</Text>
        <Text style={styles.storeDate}>
          {new Date(item.created_at).toLocaleDateString('de-DE')}
        </Text>
      </View>
      <TouchableOpacity
        style={styles.iconButton}
        onPress={() => navigation.navigate('StoreSort', { storeId: item.id, storeName: item.name })}
      >
        <Ionicons name="swap-vertical-outline" size={18} color="#2E7D32" />
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.iconButton}
        onPress={() => handleOpenModal(item)}
      >
        <Ionicons name="create-outline" size={18} color="#666" />
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.iconButton}
        onPress={() => handleDelete(item)}
      >
        <Ionicons name="trash-outline" size={18} color="#d32f2f" />
      </TouchableOpacity>
    </View>
  );

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#2E7D32" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Suchleiste */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <Ionicons name="search-outline" size={20} color="#666" style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Läden suchen..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#999"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity
              onPress={() => setSearchQuery('')}
              style={styles.clearButton}
            >
              <Ionicons name="close-circle" size={20} color="#666" />
            </TouchableOpacity>
          )}
        </View>
      </View>

      <FlatList
        data={filteredStores}
        renderItem={renderStore}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="storefront-outline" size={64} color="#ccc" />
            <Text style={styles.emptyText}>
              {searchQuery ? 'Keine Läden gefunden' : 'Keine Läden vorhanden'}
            </Text>
            <Text style={styles.emptySubtext}>
              {searchQuery ? 'Versuche einen anderen Suchbegriff' : 'Erstelle deinen ersten Laden'}
            </Text>
          </View>
        }
      />


      <Modal
        visible={modalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              {editingStore ? 'Laden bearbeiten' : 'Neuer Laden'}
            </Text>

            <TextInput
              style={styles.input}
              placeholder="Laden-Name (z.B. REWE, Aldi)"
              value={storeName}
              onChangeText={setStoreName}
              autoFocus
            />

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setModalVisible(false)}
              >
                <Text style={styles.cancelButtonText}>Abbrechen</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={styles.saveButtonText}>Speichern</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    padding: 10,
  },
  storeCard: {
    backgroundColor: '#F1F8E9',
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#C8E6C9',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  storeInfo: {
    flex: 1,
  },
  storeName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#2E7D32',
    marginBottom: 2,
  },
  storeDate: {
    fontSize: 11,
    color: '#689F38',
  },
  iconButton: {
    padding: 6,
    marginLeft: 2,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingTop: 60,
  },
  emptyText: {
    fontSize: 20,
    color: '#999',
    marginTop: 20,
    fontWeight: '600',
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 10,
  },
  fab: {
    position: 'absolute',
    right: 20,
    bottom: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#2E7D32',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    width: '100%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#333',
    marginBottom: 20,
  },
  input: {
    backgroundColor: '#f5f5f5',
    padding: 15,
    borderRadius: 8,
    fontSize: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 10,
  },
  modalButton: {
    flex: 1,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#f5f5f5',
  },
  cancelButtonText: {
    color: '#666',
    fontSize: 16,
    fontWeight: '600',
  },
  saveButton: {
    backgroundColor: '#2E7D32',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  searchContainer: {
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    paddingVertical: 0,
    outline: 'none',
    borderWidth: 0,
  },
  clearButton: {
    marginLeft: 8,
    padding: 2,
  },
});

export default StoresScreen;

